import {
  MaybeRef,
  Ref,
  UnwrapRef,
  computed,
  onMounted,
  readonly,
  ref,
  unref,
  watch,
} from 'vue';
import type { Vector2 } from '@kitware/vtk.js/types';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { frameOfReferenceToImageSliceAndAxis } from '@/src/utils/frameOfReference';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { Maybe } from '@/src/types';
import { useToolStore } from '@/src/store/tools';
import { Tools } from '@/src/store/tools/types';
import { AnnotationToolStore } from '@/src/store/tools/useAnnotationTool';
import { getCSSCoordinatesFromEvent } from '@/src/utils/vtk-helpers';
import { LPSAxis } from '@/src/types/lps';
import { AnnotationTool, ToolID } from '@/src/types/annotation-tool';
import vtkAbstractWidget from '@kitware/vtk.js/Widgets/Core/AbstractWidget';
import { usePopperState } from '@/src/composables/usePopperState';
import {
  ContextMenuEvent,
  vtkAnnotationToolWidget,
} from '@/src/vtk/ToolWidgetUtils/types';
import { ImageMetadata } from '@/src/types/image';
import { View } from '@/src/core/vtk/types';
import { watchImmediate } from '@vueuse/core';
import { useSegmentStore } from '@/src/segmentation/segments';

const SHOW_OVERLAY_DELAY = 250; // milliseconds

// does the tools's frame of reference match
// the view's axis
export const doesToolFrameMatchViewAxis = <Tool extends AnnotationTool>(
  viewAxis: MaybeRef<LPSAxis>,
  tool: Partial<Tool>,
  imageMetadata: MaybeRef<ImageMetadata>
) => {
  if (!tool.frameOfReference) return false;

  const toolAxis = frameOfReferenceToImageSliceAndAxis(
    tool.frameOfReference,
    unref(imageMetadata),
    {
      allowOutOfBoundsSlice: true,
    }
  );
  return !!toolAxis && toolAxis.axis === unref(viewAxis);
};

/** Everything that renders a shape or its selection outline shares this. */
export const isToolVisible = (
  tool: Pick<AnnotationTool, 'hidden' | 'placing' | 'segmentId'>
) =>
  !tool.hidden &&
  // Keep the active placement widget alive until it commits. Completed
  // shapes inherit the segment's visibility without changing child flags.
  (tool.placing ||
    (useSegmentStore().segments.getSegment(tool.segmentId)?.visible ?? true));

export const useCurrentTools = <S extends AnnotationToolStore>(
  toolStore: S,
  viewAxis: Ref<LPSAxis>,
  placingToolWhitelist: Ref<Array<ToolID>>,
  // For cine: the current frame. A tool matches when its own frame matches
  // or is unset (volume tools have no frame). Pass undefined on volume views.
  viewFrame?: MaybeRef<Maybe<number>>
) => {
  const { currentImageID, currentImageMetadata } = useCurrentImage();
  return computed(() => {
    const curImageID = currentImageID.value;
    const frame = unref(viewFrame);

    type ToolType = S['tools'][number];
    return (toolStore.tools as Array<ToolType>).filter((tool) => {
      // ensure that we don't show placing tools from other views
      if (tool.placing && !placingToolWhitelist.value.includes(tool.id))
        return false;

      if (frame != null && tool.frame != null && tool.frame !== frame)
        return false;

      // only show tools for the current image,
      // current view axis and not hidden
      return (
        tool.imageID === curImageID &&
        doesToolFrameMatchViewAxis(viewAxis, tool, currentImageMetadata) &&
        isToolVisible(tool)
      );
    });
  });
};

// --- Context Menu --- //

export const useContextMenu = () => {
  const contextMenu = ref<{
    open: (id: ToolID, e: ContextMenuEvent) => void;
  } | null>(null);
  const openContextMenu = (toolID: ToolID, event: ContextMenuEvent) => {
    if (!contextMenu.value)
      throw new Error('contextMenu component does not exist');
    contextMenu.value.open(toolID, event);
  };

  return { contextMenu, openContextMenu };
};

export const useRightClickContextMenu = (
  emit: (event: 'contextmenu', ...args: any[]) => void,
  widget: MaybeRef<vtkAnnotationToolWidget | null>
) => {
  onVTKEvent(widget, 'onRightClickEvent', (eventData) => {
    const displayXY = getCSSCoordinatesFromEvent(eventData);
    if (displayXY) {
      emit('contextmenu', {
        displayXY,
        widgetActions: eventData.widgetActions,
      } satisfies ContextMenuEvent);
    }
  });
};

// --- Hover --- //

export const useHoverEvent = (
  emit: (event: 'widgetHover', ...args: any[]) => void,
  widget: MaybeRef<vtkAnnotationToolWidget | null>
) => {
  onVTKEvent(widget, 'onHoverEvent', (eventData: any) => {
    const displayXY = getCSSCoordinatesFromEvent(eventData);
    if (displayXY) {
      emit('widgetHover', {
        displayXY,
        hovering: eventData.hovering,
      });
    }
  });
};

export type OverlayInfo =
  | {
      visible: false;
    }
  | {
      visible: true;
      toolID: ToolID;
      displayXY: Vector2;
    };

// Maintains list of tools' hover states.
// If one tool hovered, overlayInfo.visible === true with toolID and displayXY.
export const useHover = (
  tools: Ref<Array<AnnotationTool>>,
  currentSlice: Ref<number>
) => {
  type Info = OverlayInfo;
  const toolHoverState = ref({}) as Ref<Record<ToolID, Info>>;

  const toolsOnCurrentSlice = computed(() =>
    tools.value.filter((tool) => tool.slice === currentSlice.value)
  );

  watch(toolsOnCurrentSlice, () => {
    // keep old hover states, default to false for new tools
    toolHoverState.value = toolsOnCurrentSlice.value.reduce(
      (toolsHovers, { id }) => {
        const state = toolHoverState.value[id] ?? {
          visible: false,
        };
        return Object.assign(toolsHovers, {
          [id]: state,
        });
      },
      {} as Record<ToolID, Info>
    );
  });

  const onHover = (id: ToolID, event: any) => {
    toolHoverState.value[id] = event.hovering
      ? {
          visible: true,
          toolID: id,
          displayXY: event.displayXY,
        }
      : {
          visible: false,
        };
  };

  // If hovering true, debounce showing overlay.
  // Immediately hide overlay if hovering false.
  const synchronousOverlayInfo = computed(() => {
    const visibleToolID = Object.keys(toolHoverState.value).find(
      (toolID) => toolHoverState.value[toolID as ToolID].visible
    ) as ToolID | undefined;

    return visibleToolID
      ? toolHoverState.value[visibleToolID]
      : ({ visible: false } as Info);
  });

  const { isSet: showOverlay, reset: resetOverlay } =
    usePopperState(SHOW_OVERLAY_DELAY);

  watch(synchronousOverlayInfo, resetOverlay);

  const toolStore = useToolStore();
  const TOOLS_WITH_HOVER = [
    Tools.Select,
    Tools.Ruler,
    Tools.Rectangle,
    Tools.Polygon,
  ];
  const overlayInfo = computed(() => {
    if (!showOverlay.value) return { visible: false } as Info;
    if (!TOOLS_WITH_HOVER.includes(toolStore.currentTool))
      return { visible: false } as Info;
    return synchronousOverlayInfo.value;
  });

  return { overlayInfo, onHover };
};

export const usePlacingAnnotationTool = (
  store: AnnotationToolStore,
  metadata: Ref<Partial<AnnotationTool>>
) => {
  const id = ref<Maybe<ToolID>>(null);
  const { selectedSegmentId } = useSegmentStore().segments;
  // The stub follows the selection, so it is drawn in the segment it will join.
  const patch = computed(() => ({
    ...metadata.value,
    segmentId: selectedSegmentId.value ?? '',
  }));

  const commit = () => {
    const id_ = id.value as Maybe<ToolID>;
    if (!id_) return;
    store.placeTool(id_);
    id.value = null;
  };

  const add = () => {
    if (id.value) throw new Error('Placing tool already exists.');
    id.value = store.addTool({
      ...patch.value,
      placing: true,
    }) as UnwrapRef<ToolID>;
  };

  const remove = () => {
    const id_ = id.value as Maybe<ToolID>;
    if (!id_) return;
    store.removeTool(id_);
    id.value = null;
  };

  watch(patch, (value) => {
    if (!id.value) return;
    store.updateTool(id.value as ToolID, value);
  });

  // The first gesture is what mints, so the shape resolves its segment as
  // placement starts rather than when it lands.
  const beginPlacement = () => {
    const id_ = id.value as Maybe<ToolID>;
    if (id_) store.resolveToolSegment(id_);
  };

  return {
    id: readonly(id),
    beginPlacement,
    commit,
    add,
    remove,
  };
};

export const useWidgetVisibility = <T extends vtkAbstractWidget>(
  widget: T,
  visible: Ref<boolean>,
  view: View
) => {
  // toggles the pickability of the ruler handles,
  // since the 3D ruler parts are visually hidden.
  watchImmediate(
    () => visible.value,
    (visibility) => {
      widget.setVisibility(visibility);
    }
  );

  onMounted(() => {
    // hide handle visibility, but not picking visibility
    widget.setHandleVisibility(false);
    view.widgetManager.renderWidgets();
    view.requestRender();
  });
};
